import Keypad from '@components/Keypad';
import { formatToNorthAmericanNumber } from '@lib/utils/formatPhone/formatToNorthAmericanNumber';
import { Input, Stack } from '@servicetitan/design-system';
import styles from './OutboundModalKeypad.module.css';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import React, { useEffect } from 'react';

interface IOutboundModalKeypadProps {
  number: string;
  setNumber: (number: string) => void;
}

/**
 * Returns a component that consists of an input field and a keypad
 * to allow users to dial in a number. The number is displayed in
 * the North American Numbering Plan format by using the function
 * formatToNorthAmericanNumber.
 *
 * The number state is kept in and passed from the parent component.
 * Upon user input, the number is set in the parent component in a
 * clean format, without any non-numeric characters.
 *
 * @param param0 IOuboundModalKeypadProps
 */
const OutboundModalKeypad: React.FC<IOutboundModalKeypadProps> = ({
  number,
  setNumber,
}) => {
  const ref = React.createRef<Input>();

  /**
   * Handles the input from the user by setting the number in the parent
   * component in a clean format, without any non-numeric characters and extra
   * country code removed.
   *
   * @param input
   */
  function handleInput(input: string) {
    setNumber(sanitizePhoneNumberTo10Digits(input));
  }

  // Focus on the input field when the component is mounted and everytime the number changes
  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, [ref, number]);

  return (
    <Stack direction='column' alignItems='center' spacing={2}>
      <Input
        className={styles.keypadInput}
        placeholder='Enter a number'
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          handleInput(event.target.value)
        }
        value={formatToNorthAmericanNumber(number)}
        ref={ref}
      />
      <Keypad
        variant='callModal'
        onKeyClick={(key) =>
          handleInput(number + key)
        } /* Accounts for the existing number and appends the user dial number at the end*/
      />
    </Stack>
  );
};

export default OutboundModalKeypad;
